import theme, { colors } from '@globals/themes/defaultTheme'
import { Box, Typography } from '@mui/material'
import Autocomplete, {
  AutocompleteRenderGroupParams,
  AutocompleteRenderValueGetItemProps,
} from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import FieldLabel from '@shared/forms/FieldLabel'
import { useT } from '@transifex/react'
import { ReactElement, ReactNode } from 'react'

export const textFieldStyle: SxProps = {
  '& input': {
    '&::placeholder': {
      color: colors.grayMedium,
      opacity: 1,
    },
  },
}

export type AutoCompleteOption<T extends object | string = string> = {
  id: string
  value: T
  icon?: ReactElement
  avatar?: ReactElement
}

export type AutoCompleteResultsZIndexPlacement = 'modal' | 'page' | 'drawer'

export type AutoCompleteRenderChipProps = ReturnType<AutocompleteRenderValueGetItemProps<false>>

type StandardAutoCompleteSearchProps<T extends object | string = string> = {
  value: AutoCompleteOption<T>[] | AutoCompleteOption<T>
  onSearchTextChanged: (searchText: string) => void
  onSelectedOptionsChanged: (option: AutoCompleteOption<T>[]) => void
  label?: ReactNode
  placeholder: string
  searchText: string
  options: AutoCompleteOption<T>[]
  renderChip: (
    option: AutoCompleteOption<T>,
    props: AutoCompleteRenderChipProps,
    index?: number,
  ) => ReactNode
  renderOption?: (props: object, option: AutoCompleteOption<T>) => ReactNode
  groupBy?: (option: AutoCompleteOption<T>) => string
  id?: string
  multiple?: boolean
  disableClose?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
}

const groupHeaderStyle: SxProps = {
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  height: 30,
  color: colors.grayPrimary,
  background: colors.grayUltraLight,
}

const placementZIndex: Record<AutoCompleteResultsZIndexPlacement, number> = {
  page: 1,
  modal: theme.zIndex.modal + 1,
  drawer: theme.zIndex.drawer + 1,
}

const StandardAutoCompleteSearch = <T extends object | string = string>({
  onSearchTextChanged,
  onSelectedOptionsChanged,
  label,
  placeholder,
  searchText,
  options,
  renderChip,
  renderOption,
  groupBy,
  id = 'autocompleteSearch',
  multiple = true,
  disabled = false,
  value,
  disableClose,
  zIndexPlacement = 'page',
}: StandardAutoCompleteSearchProps<T>) => {
  const t = useT()
  const renderGroup = (params: AutocompleteRenderGroupParams) => (
    <Stack key={params.key}>
      <Typography variant="caption" sx={groupHeaderStyle}>
        {params.group}
      </Typography>
      <Box>{params.children}</Box>
    </Stack>
  )

  return (
    <Stack direction="column" useFlexGap gap={0.25}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Autocomplete
        sx={textFieldStyle}
        multiple={multiple}
        disableCloseOnSelect={disableClose}
        id={id}
        filterOptions={option => option}
        value={value}
        options={options}
        autoComplete
        fullWidth
        groupBy={groupBy}
        renderGroup={groupBy ? renderGroup : undefined}
        getOptionLabel={option =>
          (!renderOption && (option as AutoCompleteOption<string>).value) || ''
        }
        inputValue={searchText}
        onInputChange={(_, newText, reason) => {
          if (reason !== 'reset') {
            onSearchTextChanged(newText)
          }
        }}
        noOptionsText={t('No results found')}
        onChange={(_, newOptions, reason) => {
          if (reason === 'clear' && !multiple) return
          if (multiple) {
            onSelectedOptionsChanged(newOptions as AutoCompleteOption<T>[])
          } else {
            onSelectedOptionsChanged(
              newOptions !== null ? [newOptions as AutoCompleteOption<T>] : [],
            )
          }
        }}
        onKeyDown={e => {
          if (!multiple && searchText === '' && value && e.key === 'Backspace') {
            onSelectedOptionsChanged([])
          }
        }}
        renderValue={(value, getItemProps) => {
          if (multiple) {
            return (value as AutoCompleteOption<T>[]).map((value, index) =>
              renderChip(value, getItemProps({ index }), index),
            )
          } else {
            return renderChip(value as AutoCompleteOption<T>, getItemProps({ index: 0 }))
          }
        }}
        renderOption={renderOption}
        renderInput={params => {
          return (
            <TextField
              {...params}
              fullWidth
              placeholder={!disabled ? placeholder : ''}
              size="small"
            />
          )
        }}
        disabled={disabled}
        slotProps={{
          popper: {
            sx: {
              zIndex: placementZIndex[zIndexPlacement],
            },
          },
        }}
      />
    </Stack>
  )
}

export default StandardAutoCompleteSearch
